// ============================================================
// FILE CONVERSION UTILITIES
// Base64 ↔ Data URL ↔ Object URL conversions
// ============================================================

/**
 * Convertit une chaîne base64 en Data URL
 */
export function base64ToDataUrl(base64: string, mimeType: string): string {
  // Si c'est déjà une data URL, la retourner telle quelle
  if (base64.startsWith('data:')) return base64;

  // Nettoyer le base64 (espaces, retours à la ligne)
  const cleanBase64 = base64.replace(/\s/g, '');

  return `data:${mimeType};base64,${cleanBase64}`;
}

/**
 * Convertit une Data URL en Object URL (blob:)
 * Plus fiable pour ouvrir des PDFs dans les navigateurs modernes
 */
export function dataUrlToObjectUrl(dataUrl: string): string {
  const matches = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
  if (!matches) throw new Error('Format de data URL invalide');

  const mimeType = matches[1];
  const base64 = matches[2];

  const byteCharacters = atob(base64);
  const byteNumbers = Array.from(byteCharacters).map((c) => c.charCodeAt(0));
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mimeType });

  return URL.createObjectURL(blob);
}

/**
 * Ouvre un PDF dans un nouvel onglet
 * Gère automatiquement la conversion base64 → data URL → object URL
 */
export function openPdfInNewTab(file: {
  url?: string;
  data?: string; // base64 brut
  type?: string; // MIME type
  name?: string;
}) {
  const raw = file.url || file.data || '';
  if (!raw) {
    console.error('Aucune donnée PDF disponible');
    return;
  }

  const dataUrl = raw.startsWith('data:') ? raw : base64ToDataUrl(raw, file.type || 'application/pdf');

  try {
    const objectUrl = dataUrlToObjectUrl(dataUrl);
    const win = window.open(objectUrl, '_blank');
    if (win) setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  } catch {
    // Fallback direct si la conversion échoue
    window.open(dataUrl, '_blank');
  }
}

/**
 * Télécharge un fichier depuis une data URL
 */
export function downloadDataUrl(dataUrl: string, fileName: string) {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Détecte le type de fichier
 */
export function detectFileType(fileName?: string, mimeType?: string): 'image' | 'pdf' | 'document' {
  if (mimeType?.startsWith('image/')) return 'image';
  if (mimeType === 'application/pdf') return 'pdf';

  const ext = fileName?.split('.').pop()?.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext ?? '')) return 'image';
  if (ext === 'pdf') return 'pdf';

  return 'document';
}
